import React, { useState, useEffect, useCallback } from 'react';
import { buildApiUrl } from '../config/environment';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Search, 
  Filter, 
  Users, 
  Mail, 
  Calendar, 
  X,
  ChevronLeft,
  ChevronRight,
  Phone
} from 'lucide-react';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import Toast from '../components/Toast';

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  status: 'active' | 'inactive';
  profilePicture?: string;
  purchasedCourses: string[];
  createdAt: string;
  updatedAt: string;
  phoneNumber?: string;
}

interface Course {
  _id: string;
  title: string;
  description: string;
  price: number;
  status: 'active' | 'inactive' | 'archived';
  thumbnailURL?: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalUsers: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  
  // Account deactivation/reactivation functionality
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [userToReactivate, setUserToReactivate] = useState<User | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  
  // Grant course access functionality
  const [userToManageCourses, setUserToManageCourses] = useState<User | null>(null);
  const [showManageCoursesModal, setShowManageCoursesModal] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [isManagingCourses, setIsManagingCourses] = useState(false);
  
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Fetch users from API
  const fetchUsers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('adminToken');
      
      if (!adminToken) {
        throw new Error('Admin token not found');
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        search: searchTerm,
        status: statusFilter !== 'all' ? statusFilter : '',
        sortBy,
        sortOrder
      });

      const response = await fetch(buildApiUrl(`/api/user/admin/all?${params}`), {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.data.users || []);
      setPagination(data.data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, sortBy, sortOrder, limit]);

  // Handle user deactivation
  const handleDeactivateUser = async (userId: string) => {
    try {
      setIsDeactivating(true);
      const adminToken = localStorage.getItem('adminToken');
      
      if (!adminToken) {
        throw new Error('Admin token not found');
      }

      const response = await fetch(buildApiUrl(`/api/user/admin/${userId}/status`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'inactive' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to deactivate user');
      }

      // Update user status in state
      setUsers(prev => prev.map(user => 
        user._id === userId ? { ...user, status: 'inactive' } : user
      ));
      setToast({
        message: 'User account deactivated successfully',
        type: 'success'
      });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to deactivate user',
        type: 'error'
      });
    } finally {
      setIsDeactivating(false);
      setShowDeactivateModal(false);
      setUserToDeactivate(null);
    }
  };

  // Handle user reactivation
  const handleReactivateUser = async (userId: string) => {
    try {
      setIsReactivating(true);
      const adminToken = localStorage.getItem('adminToken');
      
      if (!adminToken) {
        throw new Error('Admin token not found');
      }

      const response = await fetch(buildApiUrl(`/api/user/admin/${userId}/status`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'active' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reactivate user');
      }

      // Update user status in state
      setUsers(prev => prev.map(user => 
        user._id === userId ? { ...user, status: 'active' } : user
      ));
      setToast({
        message: 'User account reactivated successfully',
        type: 'success'
      });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to reactivate user',
        type: 'error'
      });
    } finally {
      setIsReactivating(false);
      setShowReactivateModal(false);
      setUserToReactivate(null);
    }
  };

  // Handle managing courses modal
  const handleOpenManageCoursesModal = async (user: User) => {
    setUserToManageCourses(user);
    setSelectedCourses(user.purchasedCourses || []);
    setShowManageCoursesModal(true);
    
    // Fetch all courses if not already loaded
    if (courses.length === 0) {
      try {
        const adminToken = localStorage.getItem('adminToken');
        const response = await fetch(buildApiUrl('/api/courses?status=all'), {
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setCourses(data.data?.courses || []);
        }
      } catch (err) {
        console.error('Failed to fetch courses:', err);
      }
    }
  };

  // Handle granting course access
  const handleGrantCourseAccess = async () => {
    if (!userToManageCourses || selectedCourses.length === 0) return;
    
    try {
      setIsManagingCourses(true);
      const adminToken = localStorage.getItem('adminToken');
      
      const response = await fetch(buildApiUrl(`/api/user/admin/${userToManageCourses._id}/grant-access`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ courseIds: selectedCourses }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to grant course access');
      }

      const data = await response.json();
      
      // Update user in state with new purchased courses count
      setUsers(prev => prev.map(user => 
        user._id === userToManageCourses._id ? { ...user, purchasedCourses: data.data.user.purchasedCourses } : user
      ));
      
      setToast({
        message: `Course access granted successfully. ${data.data.newCourses} new courses added.`,
        type: 'success'
      });
      
      setShowManageCoursesModal(false);
      setUserToManageCourses(null);
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to grant course access',
        type: 'error'
      });
    } finally {
      setIsManagingCourses(false);
    }
  };

  // Handle revoking course access
  const handleRevokeCourseAccess = async () => {
    if (!userToManageCourses) return;
    
    const coursesToRevoke = courses
      .filter(course => !selectedCourses.includes(course._id))
      .map(course => course._id);
    
    if (coursesToRevoke.length === 0) return;
    
    try {
      setIsManagingCourses(true);
      const adminToken = localStorage.getItem('adminToken');
      
      const response = await fetch(buildApiUrl(`/api/user/admin/${userToManageCourses._id}/revoke-access`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ courseIds: coursesToRevoke }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to revoke course access');
      }

      const data = await response.json();
      
      // Update user in state with new purchased courses count
      setUsers(prev => prev.map(user => 
        user._id === userToManageCourses._id ? { ...user, purchasedCourses: data.data.user.purchasedCourses } : user
      ));
      
      setToast({
        message: `Course access revoked successfully. ${data.data.coursesRevoked} courses removed.`,
        type: 'success'
      });
      
      setShowManageCoursesModal(false);
      setUserToManageCourses(null);
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to revoke course access',
        type: 'error'
      });
    } finally {
      setIsManagingCourses(false);
    }
  };

  // Handle search and filters
  const handleSearch = () => {
    setCurrentPage(1);
    fetchUsers(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSortBy('createdAt');
    setSortOrder('desc');
    setCurrentPage(1);
    fetchUsers(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchUsers(page);
  };

  useEffect(() => {
    fetchUsers(currentPage);
  }, [currentPage, fetchUsers]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 xxs:h-12 xxs:w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 text-sm xxs:text-base">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error && users.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-600 mb-4 text-sm xxs:text-base">{error}</p>
          <button 
            onClick={() => fetchUsers(1)}
            className="bg-red-600 text-white px-3 xxs:px-4 py-2 rounded-lg hover:bg-red-700 text-sm xxs:text-base"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 xxs:px-4 sm:px-6 lg:px-8 py-4 xxs:py-6">
          <div className="flex flex-col xxs:flex-row xxs:items-center xxs:justify-between space-y-4 xxs:space-y-0">
            <div>
              <h1 className="text-2xl xxs:text-3xl font-bold text-gray-900">User Management</h1>
              <p className="mt-2 text-gray-600 text-sm xxs:text-base">Manage all registered users, view profiles, and control access</p>
            </div>
            <div className="flex space-x-2 xxs:space-x-3">
              <Link
                to="/admin/dashboard"
                className="inline-flex items-center px-3 xxs:px-4 py-2 border border-gray-300 text-xs xxs:text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <ArrowLeft className="h-3 w-3 xxs:h-4 xxs:w-4 mr-1 xxs:mr-2" />
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters and Search */}
      <div className="max-w-7xl mx-auto px-3 xxs:px-4 sm:px-6 lg:px-8 py-4 xxs:py-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Filter Header */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 px-3 xxs:px-4 sm:px-6 py-3 xxs:py-4 border-b border-gray-100">
            <div className="flex flex-col xxs:flex-row xxs:items-center xxs:justify-between space-y-3 xxs:space-y-0">
              <div className="flex items-center space-x-2 xxs:space-x-3">
                <Filter className="h-4 w-4 xxs:h-5 xxs:w-5 text-red-600" />
                <h3 className="text-base xxs:text-lg font-semibold text-gray-900">User Management</h3>
                {(searchTerm || statusFilter !== 'all') && (
                  <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full">
                    Filtered
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2 xxs:space-x-3">
                {(searchTerm || statusFilter !== 'all') && (
                  <button
                    onClick={handleClearFilters}
                    className="flex items-center space-x-1 xxs:space-x-2 px-2 xxs:px-3 py-1 xxs:py-1.5 text-xs xxs:text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                  >
                    <X className="h-3 w-3 xxs:h-4 xxs:w-4" />
                    <span>Clear filters</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filter Options */}
          <div className="p-3 xxs:p-4 sm:p-6">
            <div className="grid grid-cols-1 xxs:grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 xxs:gap-4 sm:gap-6">
              {/* Search */}
              <div className="xxs:col-span-1 sm:col-span-2 space-y-2">
                <label className="block text-xs xxs:text-sm font-semibold text-gray-700">
                  Search Users
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 xxs:pl-4 flex items-center pointer-events-none">
                    <Search className="h-3 w-3 xxs:h-4 xxs:w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="block w-full pl-8 xxs:pl-10 pr-4 py-2 xxs:py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-red-100 focus:border-red-500 transition-all duration-200 text-sm xxs:text-base"
                    placeholder="Search by name or email..."
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      title="Clear search"
                      className="absolute inset-y-0 right-0 pr-3 xxs:pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-3 w-3 xxs:h-4 xxs:w-4" />
                    </button>
                  )}
                </div>
              </div>


              {/* Status Filter */}
              <div className="space-y-2">
                <label htmlFor="statusFilter" className="block text-sm font-semibold text-gray-700">
                  Status
                </label>
                <div className="relative">
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-red-100 focus:border-red-500 transition-all duration-200 appearance-none bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Sort */}
              <div className="space-y-2">
                <label htmlFor="sortByOrder" className="block text-sm font-semibold text-gray-700">
                  Sort By
                </label>
                <div className="relative">
                  <select
                    id="sortByOrder"
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order as 'asc' | 'desc');
                    }}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-red-100 focus:border-red-500 transition-all duration-200 appearance-none bg-white"
                  >
                    <option value="createdAt-desc">Newest First</option>
                    <option value="createdAt-asc">Oldest First</option>
                    <option value="name-asc">Name A-Z</option>
                    <option value="name-desc">Name Z-A</option>
                    <option value="email-asc">Email A-Z</option>
                    <option value="email-desc">Email Z-A</option>
                    <option value="status-asc">Status: Active → Inactive</option>
                    <option value="status-desc">Status: Inactive → Active</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Button */}
            <div className="mt-6">
              <button
                onClick={handleSearch}
                className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
              >
                <Search className="h-4 w-4 mr-2" />
                Search Users
              </button>
            </div>

            {/* Results Summary */}
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">{pagination?.totalUsers || 0}</span> total users
                  {pagination && (
                    <span className="ml-2">
                      (Page {pagination.currentPage} of {pagination.totalPages})
                    </span>
                  )}
                </div>
                {(searchTerm || statusFilter !== 'all') && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Filtered by:</span>
                    <div className="flex flex-wrap gap-2">
                      {searchTerm && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          "{searchTerm}"
                        </span>
                      )}
                      {statusFilter !== 'all' && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Users Grid */}
        <div className="mt-4 xxs:mt-6 sm:mt-8">
          {users.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Courses
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {user.profilePicture ? (
                                <img
                                  className="h-10 w-10 rounded-full object-cover"
                                  src={user.profilePicture}
                                  alt={user.name}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                  <Users className="h-5 w-5 text-gray-600" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <Mail className="h-3 w-3 mr-1" />
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="max-w-32 truncate" title={user.phoneNumber || 'N/A'}>
                            {user.phoneNumber || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {user.purchasedCourses?.length || 0} courses
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(user.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {user.role !== 'admin' && (
                              <button
                                onClick={() => handleOpenManageCoursesModal(user)}
                                className="text-blue-600 hover:text-blue-900 transition-colors duration-200 text-sm font-medium"
                                title="Manage Course Access"
                              >
                                Manage Courses
                              </button>
                            )}
                            {user.role !== 'admin' && user.status === 'active' && (
                              <button
                                onClick={() => {
                                  setUserToDeactivate(user);
                                  setShowDeactivateModal(true);
                                }}
                                className="text-orange-600 hover:text-orange-900 transition-colors duration-200 text-sm font-medium"
                                title="Deactivate Account"
                              >
                                Deactivate
                              </button>
                            )}
                            {user.role !== 'admin' && user.status === 'inactive' && (
                              <button
                                onClick={() => {
                                  setUserToReactivate(user);
                                  setShowReactivateModal(true);
                                }}
                                className="text-green-600 hover:text-green-900 transition-colors duration-200 text-sm font-medium"
                                title="Reactivate Account"
                              >
                                Reactivate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3 xxs:space-y-4">
                {users.map((user) => (
                  <div key={user._id} className="bg-white rounded-lg shadow-sm border p-3 xxs:p-4">
                    <div className="flex items-start space-x-3 xxs:space-x-4">
                      <div className="flex-shrink-0 h-10 w-10 xxs:h-12 xxs:w-12">
                        {user.profilePicture ? (
                          <img
                            className="h-10 w-10 xxs:h-12 xxs:w-12 rounded-full object-cover"
                            src={user.profilePicture}
                            alt={user.name}
                          />
                        ) : (
                          <div className="h-10 w-10 xxs:h-12 xxs:w-12 rounded-full bg-gray-300 flex items-center justify-center">
                            <Users className="h-5 w-5 xxs:h-6 xxs:w-6 text-gray-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm xxs:text-base font-medium text-gray-900 truncate">{user.name}</h3>
                          <div className="flex items-center space-x-1 xxs:space-x-2">
                            {user.role !== 'admin' && (
                              <button
                                onClick={() => handleOpenManageCoursesModal(user)}
                                className="text-blue-600 hover:text-blue-900 transition-colors duration-200 text-xs xxs:text-sm font-medium"
                                title="Manage Course Access"
                              >
                                Manage Courses
                              </button>
                            )}
                            {user.role !== 'admin' && user.status === 'active' && (
                              <button
                                onClick={() => {
                                  setUserToDeactivate(user);
                                  setShowDeactivateModal(true);
                                }}
                                className="text-orange-600 hover:text-orange-900 transition-colors duration-200 text-xs xxs:text-sm font-medium px-2 py-1"
                                title="Deactivate Account"
                              >
                                Deactivate
                              </button>
                            )}
                            {user.role !== 'admin' && user.status === 'inactive' && (
                              <button
                                onClick={() => {
                                  setUserToReactivate(user);
                                  setShowReactivateModal(true);
                                }}
                                className="text-green-600 hover:text-green-900 transition-colors duration-200 text-xs xxs:text-sm font-medium px-2 py-1"
                                title="Reactivate Account"
                              >
                                Reactivate
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="text-xs xxs:text-sm text-gray-500 flex items-center mb-2">
                          <Mail className="h-3 w-3 mr-1" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        <div className="text-xs xxs:text-sm text-gray-500 flex items-center mb-2">
                          <Phone className="h-3 w-3 mr-1" />
                          <span className="truncate">{user.phoneNumber || 'N/A'}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 xxs:gap-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}>
                            {user.status}
                          </span>
                          <span className="text-xs xxs:text-sm text-gray-500">
                            {user.purchasedCourses?.length || 0} courses
                          </span>
                        </div>
                        <div className="mt-2 xxs:mt-3 text-xs xxs:text-sm text-gray-500 flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          Joined {formatDate(user.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8 xxs:py-12">
              <div className="mx-auto h-10 w-10 xxs:h-12 xxs:w-12 text-gray-400">
                <Users className="h-10 w-10 xxs:h-12 xxs:w-12" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
              <p className="mt-1 text-xs xxs:text-sm text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No users have registered yet.'
                }
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-4 xxs:mt-6 flex items-center justify-between">
            <div className="flex-1 flex justify-between md:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                className="relative inline-flex items-center px-3 xxs:px-4 py-2 border border-gray-300 text-xs xxs:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className="ml-2 xxs:ml-3 relative inline-flex items-center px-3 xxs:px-4 py-2 border border-gray-300 text-xs xxs:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden md:flex-1 md:flex md:items-center md:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">{(currentPage - 1) * limit + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * limit, pagination.totalUsers)}
                  </span>
                  {' '}of{' '}
                  <span className="font-medium">{pagination.totalUsers}</span>
                  {' '}results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    title="Previous page"
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-red-50 border-red-500 text-red-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    title="Next page"
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Deactivate Confirmation Modal */}
      {showDeactivateModal && userToDeactivate && (
        <DeleteConfirmationModal
          isOpen={showDeactivateModal}
          onClose={() => setShowDeactivateModal(false)}
          onConfirm={() => handleDeactivateUser(userToDeactivate._id)}
          title="Deactivate Account"
          message={`Are you sure you want to deactivate "${userToDeactivate.name}"'s account? The user will not be able to log in, but their data will be preserved.`}
          confirmText="Deactivate Account"
          cancelText="Cancel"
          isLoading={isDeactivating}
        />
      )}

      {/* Reactivate Confirmation Modal */}
      {showReactivateModal && userToReactivate && (
        <DeleteConfirmationModal
          isOpen={showReactivateModal}
          onClose={() => setShowReactivateModal(false)}
          onConfirm={() => handleReactivateUser(userToReactivate._id)}
          title="Reactivate Account"
          message={`Are you sure you want to reactivate "${userToReactivate.name}"'s account? The user will regain access to their account and all purchased courses.`}
          confirmText="Reactivate Account"
          cancelText="Cancel"
          isLoading={isReactivating}
        />
      )}

      {/* Manage Courses Modal */}
      {showManageCoursesModal && userToManageCourses && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Manage Course Access for {userToManageCourses.name}
                </h3>
                <button
                  onClick={() => setShowManageCoursesModal(false)}
                  title="Close modal"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Select the courses this user should have access to. Checked courses will be granted, unchecked courses will be revoked.
                </p>
              </div>

              <div className="max-h-96 overflow-y-auto border rounded-md">
                {courses.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {courses.map((course) => (
                      <div key={course._id} className="p-4 flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id={`course-${course._id}`}
                          checked={selectedCourses.includes(course._id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedCourses(prev => [...prev, course._id]);
                            } else {
                              setSelectedCourses(prev => prev.filter(id => id !== course._id));
                            }
                          }}
                          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <label htmlFor={`course-${course._id}`} className="text-sm font-medium text-gray-900 cursor-pointer">
                            {course.title}
                          </label>
                          <p className="text-sm text-gray-500 truncate">{course.description}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-gray-500">${course.price}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              course.status === 'active' ? 'bg-green-100 text-green-800' :
                              course.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {course.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    Loading courses...
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowManageCoursesModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGrantCourseAccess}
                  disabled={isManagingCourses || selectedCourses.length === 0}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isManagingCourses ? 'Updating...' : 'Grant Selected Courses'}
                </button>
                <button
                  onClick={handleRevokeCourseAccess}
                  disabled={isManagingCourses}
                  className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isManagingCourses ? 'Updating...' : 'Revoke Unselected Courses'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AdminUsersPage; 